<template>
  <div class="checkout-products">
    <div class="py-8">
      <h2 class="text-xl sm:text-2xl font-extrabold text-custom-text">
        {{ t("title") }}
      </h2>
      <p class="sm:text-lg font-medium mt-4 text-custom-grey-1">
        {{ t("subtitle") }}
      </p>
    </div>

    <div v-if="products.length === 0" class="empty-state py-8 text-center">
      <p class="text-custom-grey-1">{{ t("no_products") }}</p>
    </div>

    <div v-else class="product-grid space-y-4">
      <!-- Single-variant: render the variant directly as a card. -->
      <!-- Multi-variant: render the group header once with radio variants. -->
      <div
        v-for="group in groupedProducts"
        :key="group.groupId"
        class="product-card border border-custom-grey-7 rounded-lg p-4 hover:border-primary transition-colors duration-200 relative"
        :class="{ 'border-primary bg-primary-50': isGroupInOrder(group) }"
      >
        <!-- Checkmark indicator -->
        <div
          v-if="isGroupInOrder(group)"
          class="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center"
        >
          <svg
            class="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div class="flex items-start gap-4">
          <button
            v-if="group.variants[0].featured_image_url"
            type="button"
            @click="openModal(group.variants[0])"
            class="flex-none w-20 h-20 rounded-md overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :aria-label="t('view_details_aria', { name: group.variants[0].title })"
          >
            <img
              :src="group.variants[0].featured_image_url ?? undefined"
              :alt="group.variants[0].title"
              class="w-full h-full object-cover"
            />
          </button>
          <button
            v-else
            type="button"
            @click="openModal(group.variants[0])"
            class="flex-none w-20 h-20 rounded-md bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :aria-label="t('view_details_aria', { name: group.variants[0].title })"
          >
            <svg
              class="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </button>
          <div class="flex-1 min-w-0">
            <button
              type="button"
              @click="openModal(group.variants[0])"
              class="font-bold text-custom-text truncate block w-full text-left hover:text-primary transition-colors focus:outline-none focus:underline"
            >
              {{ group.variants[0].title }}
            </button>
            <p
              v-if="group.variants[0].brief_description"
              class="text-sm text-custom-grey-1 mt-1 line-clamp-2"
            >
              {{ group.variants[0].brief_description }}
            </p>
            <button
              v-if="group.variants[0].description"
              type="button"
              @click="openModal(group.variants[0])"
              class="text-xs text-primary hover:text-primary-700 mt-1 focus:outline-none focus:underline"
            >
              {{ t("view_details") }}
            </button>

            <!-- Single-variant: classic price + add/remove row -->
            <div
              v-if="group.variants.length === 1"
              class="mt-2 flex items-center justify-between"
            >
              <div>
                <span class="font-bold text-custom-text">
                  {{
                    formatPriceCents(
                      group.variants[0].price_in_cents,
                      group.variants[0].price_currency
                    )
                  }}
                </span>
                <span class="text-xs text-custom-grey-1 ml-1">
                  {{ t("vat") }}
                </span>
              </div>
              <button
                type="button"
                @click="toggleProduct(group.variants[0])"
                class="px-4 py-2 text-sm font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                :class="
                  isInOrder(group.variants[0])
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-primary text-white hover:bg-primary-700'
                "
                :disabled="saving"
                :aria-label="
                  isInOrder(group.variants[0])
                    ? t('remove_aria', { name: group.variants[0].title })
                    : t('add_aria', { name: group.variants[0].title })
                "
              >
                <span v-if="saving && savingProductId === group.variants[0].id">
                  {{ t("saving") }}
                </span>
                <span v-else-if="isInOrder(group.variants[0])">
                  {{ t("remove") }}
                </span>
                <span v-else>
                  {{ t("add_to_order") }}
                </span>
              </button>
            </div>

            <!-- Multi-variant: radio list -->
            <fieldset
              v-else
              class="mt-3 space-y-2"
              :disabled="saving"
            >
              <legend class="sr-only">
                {{ t("choose_option_for", { name: group.variants[0].title }) }}
              </legend>
              <label
                v-for="variant in group.variants"
                :key="variant.id"
                class="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-custom-grey-7 cursor-pointer hover:border-primary transition-colors"
                :class="{
                  'border-primary bg-white': selectedVariantId(group) === variant.id,
                }"
              >
                <span class="flex items-center gap-3 min-w-0">
                  <input
                    type="radio"
                    :name="`product-group-${group.groupId}`"
                    :value="variant.id"
                    :checked="selectedVariantId(group) === variant.id"
                    @change="selectVariant(group, variant)"
                    class="text-primary focus:ring-primary"
                  />
                  <span class="text-sm text-custom-text truncate">
                    {{ variantLabel(variant) }}
                  </span>
                </span>
                <span class="text-sm font-semibold text-custom-text whitespace-nowrap">
                  {{
                    formatPriceCents(
                      variant.price_in_cents,
                      variant.price_currency
                    )
                  }}
                </span>
              </label>
              <div class="flex items-center justify-between pt-1">
                <span class="text-xs text-custom-grey-1">{{ t("vat") }}</span>
                <button
                  v-if="isGroupInOrder(group)"
                  type="button"
                  @click="removeGroup(group)"
                  class="text-sm font-semibold text-red-700 hover:text-red-800 focus:outline-none focus:underline disabled:opacity-50"
                  :disabled="saving"
                >
                  {{ t("remove") }}
                </button>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Details Modal -->
    <ProductModal
      :product="selectedProduct"
      :is-in-order="selectedProduct ? isInOrder(selectedProduct) : false"
      :saving="saving && savingProductId === selectedProduct?.id"
      @close="closeModal"
      @toggle="toggleProductFromModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { IProduct, IOrderLine } from "@/scripts/application/types";
import { useI18n } from "vue-i18n";
import { useCurrency } from "@/scripts/application/composables/useCurrency";
import ProductModal from "./ProductModal.vue";

interface IProductGroup {
  groupId: number;
  variants: IProduct[];
}

const props = defineProps<{
  products: IProduct[];
  orderLines: IOrderLine[];
  saving?: boolean;
}>();

const emit = defineEmits<{
  (event: "add-product", product: IProduct): void;
  (event: "remove-product", product: IProduct): void;
  (event: "swap-product", payload: { from: IProduct; to: IProduct }): void;
}>();

const { t } = useI18n({ inheritLocale: true, useScope: "local" });
const { formatPriceCents } = useCurrency();

const savingProductId = ref<number | null>(null);
const selectedProduct = ref<IProduct | null>(null);

const orderLineProductIds = computed(() =>
  props.orderLines
    .filter((line) => !line._destroy)
    .map((line) => line.orderable_id)
);

const groupedProducts = computed<IProductGroup[]>(() => {
  const byGroup = new Map<number, IProduct[]>();
  for (const product of props.products) {
    const list = byGroup.get(product.product_group_id) ?? [];
    list.push(product);
    byGroup.set(product.product_group_id, list);
  }
  return Array.from(byGroup.entries()).map(([groupId, variants]) => ({
    groupId,
    variants: variants.slice().sort((a, b) => a.price_in_cents - b.price_in_cents),
  }));
});

function isInOrder(product: IProduct): boolean {
  return orderLineProductIds.value.includes(product.id);
}

function selectedVariantId(group: IProductGroup): number | null {
  const found = group.variants.find((v) => isInOrder(v));
  return found?.id ?? null;
}

function isGroupInOrder(group: IProductGroup): boolean {
  return selectedVariantId(group) !== null;
}

function variantLabel(variant: IProduct): string {
  return variant.name?.trim() || formatPriceCents(variant.price_in_cents, variant.price_currency);
}

function toggleProduct(product: IProduct) {
  savingProductId.value = product.id;
  if (isInOrder(product)) {
    emit("remove-product", product);
  } else {
    emit("add-product", product);
  }
}

function selectVariant(group: IProductGroup, variant: IProduct) {
  if (isInOrder(variant)) return;
  savingProductId.value = variant.id;
  const current = group.variants.find((v) => isInOrder(v));
  if (current) {
    emit("swap-product", { from: current, to: variant });
  } else {
    emit("add-product", variant);
  }
}

function removeGroup(group: IProductGroup) {
  const current = group.variants.find((v) => isInOrder(v));
  if (!current) return;
  savingProductId.value = current.id;
  emit("remove-product", current);
}

function openModal(product: IProduct) {
  selectedProduct.value = product;
}

function closeModal() {
  selectedProduct.value = null;
}

function toggleProductFromModal(product: IProduct) {
  toggleProduct(product);
}
</script>

<i18n lang="yaml">
en:
  title: "Upgrade your learning experience"
  subtitle: "Enhance your learning outcome with these addons:"
  no_products: "No additional products available for this course"
  add_to_order: "Add to order"
  remove: "Remove"
  back: "Back"
  continue: "Continue to payment"
  saving: "Saving..."
  view_details: "View details"
  view_details_aria: "View details for {name}"
  add_aria: "Add {name} to your order"
  remove_aria: "Remove {name} from your order"
  choose_option_for: "Choose an option for {name}"
  vat: "incl. VAT"
nb:
  title: "Oppgrader kursopplevelsen din"
  subtitle: "Få bedre læringsutbytte med disse tilleggene:"
  no_products: "Ingen ekstra produkter tilgjengelig for dette kurset"
  add_to_order: "Legg til"
  remove: "Fjern"
  back: "Tilbake"
  continue: "Fortsett til betaling"
  saving: "Lagrer..."
  view_details: "Se detaljer"
  view_details_aria: "Se detaljer for {name}"
  add_aria: "Legg {name} til bestillingen din"
  remove_aria: "Fjern {name} fra bestillingen din"
  choose_option_for: "Velg et alternativ for {name}"
  vat: "inkl. MVA"
uk:
  title: "Покращте свій досвід навчання"
  subtitle: "Покращте своє навчання з цими додатками"
  no_products: "Немає додаткових продуктів для цього курсу"
  add_to_order: "Додати"
  remove: "Видалити"
  back: "Назад"
  continue: "Перейти до оплати"
  saving: "Збереження..."
  view_details: "Переглянути деталі"
  view_details_aria: "Переглянути деталі для {name}"
  add_aria: "Додати {name} до замовлення"
  remove_aria: "Видалити {name} з замовлення"
  choose_option_for: "Виберіть варіант для {name}"
  vat: "з ПДВ"
es:
  title: "Mejora tu experiencia de aprendizaje"
  subtitle: "Potencia tu aprendizaje con estos complementos:"
  no_products: "No hay productos adicionales disponibles para este curso"
  add_to_order: "Añadir"
  remove: "Eliminar"
  back: "Volver"
  continue: "Continuar al pago"
  saving: "Guardando..."
  view_details: "Ver detalles"
  view_details_aria: "Ver detalles de {name}"
  add_aria: "Añadir {name} a tu pedido"
  remove_aria: "Eliminar {name} de tu pedido"
  choose_option_for: "Elige una opción para {name}"
  vat: "IVA incl."
pl:
  title: "Ulepsz swoje doświadczenie edukacyjne"
  subtitle: "Wzbogać swoją naukę o te dodatki:"
  no_products: "Brak dodatkowych produktów dla tego kursu"
  add_to_order: "Dodaj"
  remove: "Usuń"
  back: "Wstecz"
  continue: "Przejdź do płatności"
  saving: "Zapisywanie..."
  view_details: "Zobacz szczegóły"
  view_details_aria: "Zobacz szczegóły dla {name}"
  add_aria: "Dodaj {name} do zamówienia"
  remove_aria: "Usuń {name} z zamówienia"
  choose_option_for: "Wybierz opcję dla {name}"
  vat: "z VAT"
</i18n>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
